import {
    parseUTCDate,
} from "./format.ts";

type BadgeVariant = "default" | "success" | "warning" | "error";

export const getJobStatusBadgeVariant = (status: string): BadgeVariant => {
    switch (status) {
        case "completed":
            return "success";
        case "processing":
            return "warning";
        case "failed":
            return "error";
        case "pending":
            return "default";
        case "cancelled":
            return "default";
        default:
            return "default";
    }
};

export const getLogLevelBadgeVariant = (level: string): BadgeVariant => {
    switch (level) {
        case "error":
        case "stderr":
            return "error";
        case "warn":
            return "warning";
        case "info":
            return "default";
        default:
            return "default";
    }
};

export const formatJobType = (jobType: string): string => {
    return jobType
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

export const formatJobDuration = (
    startedAt: string | null,
    completedAt: string | null,
): string => {
    if (!startedAt || !completedAt) return "\u2014";
    const durationMs = parseUTCDate(completedAt).getTime() -
        parseUTCDate(startedAt).getTime();
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60_000) return `${(durationMs / 1000).toFixed(1)}s`;
    if (durationMs < 3_600_000) {
        return `${Math.floor(durationMs / 60_000)}m ${
            Math.floor((durationMs % 60_000) / 1000)
        }s`;
    }
    return `${Math.floor(durationMs / 3_600_000)}h ${
        Math.floor((durationMs % 3_600_000) / 60_000)
    }m`;
};

export const formatElapsed = (startedAt: string): string => {
    const elapsed = Date.now() - parseUTCDate(startedAt).getTime();
    if (elapsed < 1000) return "<1s";
    if (elapsed < 60_000) return `${Math.floor(elapsed / 1000)}s`;
    return `${Math.floor(elapsed / 60_000)}m ${
        Math.floor((elapsed % 60_000) / 1000)
    }s`;
};

export const formatMs = (ms: number): string => {
    if (ms < 60_000) return `${ms / 1000}s`;
    if (ms < 3_600_000) return `${ms / 60_000}m`;
    return `${ms / 3_600_000}h`;
};

export const formatLocalTimestamp = (dateString: string): string => {
    const date = parseUTCDate(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
        return date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        });
    }
    return date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
    }) + " " + date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
};

export const tryParseJSON = (value: string | null): string => {
    if (!value) return "\u2014";
    try {
        return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
        return value;
    }
};
